import React from "react";
import { Link } from "react-router-dom";
import useSeo from "../hooks/useSeo";
import { Cookie, Settings, Mail } from "lucide-react";

const Section = ({ id, title, children }) => (
  <div id={id} className="mb-10 scroll-mt-24">
    <h2 className="font-display font-semibold text-2xl text-brand-dark tracking-tight">{title}</h2>
    <div className="mt-3 text-brand-text2 leading-relaxed space-y-3">{children}</div>
  </div>
);

const TableCookie = ({ rows }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm border-collapse">
      <thead className="bg-slate-50">
        <tr>
          <th className="text-left p-2 border border-slate-200">Cookie / Lagring</th>
          <th className="text-left p-2 border border-slate-200">Udbyder</th>
          <th className="text-left p-2 border border-slate-200">Formål</th>
          <th className="text-left p-2 border border-slate-200">Varighed</th>
          <th className="text-left p-2 border border-slate-200">Type</th>
        </tr>
      </thead>
      <tbody className="bg-white">
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="p-2 border border-slate-200 font-mono text-xs">{r[0]}</td>
            <td className="p-2 border border-slate-200">{r[1]}</td>
            <td className="p-2 border border-slate-200">{r[2]}</td>
            <td className="p-2 border border-slate-200">{r[3]}</td>
            <td className="p-2 border border-slate-200">{r[4]}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NECESSARY = [
  ["ema_admin_token", "EMA Kurer", "Login-session for administratorer (JWT)", "12 timer", "Persistent (localStorage)"],
  ["ema_driver_token", "EMA Kurer", "Login-session for chauffører (JWT)", "30 dage", "Persistent (localStorage)"],
  ["ema_customer_token", "EMA Kurer", "Login-session for kunder (magic link)", "7 dage", "Persistent (localStorage)"],
  ["ema_b2b_token", "EMA Kurer", "Login-session for B2B-portal brugere (JWT)", "12 timer", "Persistent (localStorage)"],
  ["ema_cookie_consent_v1", "EMA Kurer", "Husk dit cookie-samtykke", "12 måneder", "Persistent (localStorage)"],
  ["XSRF-TOKEN", "EMA Kurer", "Beskytter mod cross-site request forgery", "Session", "Cookie"],
  ["__stripe_*", "Stripe Payments Europe Ltd. (Irland)", "Betaling og bedrageribeskyttelse på checkout-siden", "Session — 1 år", "Cookie (kun ved betaling)"],
];

const ANALYTICS = [
  ["ph_*", "PostHog Inc. (USA, SCC)", "Anonym produkt-statistik, fejldetektion, brugerrejse-analyse", "12 måneder", "Persistent (localStorage)"],
  ["ph_session_*", "PostHog Inc.", "Session-replay (kun hvis du har givet samtykke til 'Session-optagelser')", "30 dage", "Persistent"],
];

export default function CookiePolicyPage() {
  useSeo({
    title: "Cookie-politik · EMA Kurer ApS",
    description:
      "Sådan bruger EMA Kurer ApS cookies og lokal lagring jf. ePrivacy-direktivet og Datatilsynets vejledning. Komplet oversigt over alle cookies og dine valgmuligheder.",
    path: "/cookie-politik",
  });

  const openSettings = () => {
    try {
      window.dispatchEvent(new CustomEvent("ema-open-cookie-settings"));
    } catch { /* ignore */ }
  };

  return (
    <div data-testid="cookie-policy-page">
      <section className="bg-brand-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="kicker text-brand-red flex items-center gap-2">
            <Cookie size={14} /> Juridisk
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight mt-3">Cookie-politik</h1>
          <p className="text-white/70 mt-3">Version 1.0 · Senest opdateret: 10. februar 2026</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <Section id="hvad" title="1. Hvad er cookies?">
          <p>
            En cookie er en lille datafil, som lagres på din enhed (computer, telefon, tablet) når du besøger en hjemmeside.
            Vi bruger også <strong>localStorage</strong>, der fungerer på lignende vis. Begge dele er omfattet af denne politik.
          </p>
          <p>
            EMA Kurer ApS følger <strong>cookiebekendtgørelsen</strong> (BEK nr. 1148 af 09/12/2011 med ændringer) og
            Datatilsynets vejledning om brug af cookies fra 2020/2023.
          </p>
        </Section>

        <Section id="samtykke" title="2. Dit samtykke">
          <p>
            Når du besøger emakurer.dk første gang, viser vi en cookie-banner hvor du kan vælge:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Acceptér alle</strong> — du tillader både nødvendige cookies og anonyme analytics-cookies</li>
            <li><strong>Afvis alle</strong> — kun de strengt nødvendige cookies sættes</li>
            <li><strong>Tilpas</strong> — vælg præcis hvilke kategorier du accepterer</li>
          </ul>
          <p>
            Vi sætter <strong>ingen ikke-nødvendige cookies før du har givet samtykke</strong>. Du kan til enhver tid trække
            dit samtykke tilbage eller ændre dit valg:
          </p>
          <button
            onClick={openSettings}
            className="mt-3 inline-flex items-center gap-2 bg-brand-red text-white px-5 py-2.5 text-sm font-bold hover:bg-red-700 transition-colors"
            data-testid="cookie-policy-open-settings"
          >
            <Settings size={14} /> Åbn cookie-indstillinger
          </button>
          <p className="text-xs text-brand-text2 mt-3">
            Du kan også slette alle cookies via din browser. Bemærk at det vil logge dig ud og nulstille dine præferencer.
          </p>
        </Section>

        <Section id="noedvendige" title="3. Nødvendige cookies (sættes altid)">
          <p>
            Disse er strengt nødvendige for at hjemmesiden fungerer — login, sikkerhed, betaling, kurv. De kan ikke fravælges,
            men kræver heller ikke samtykke jf. ePrivacy-direktivet art. 5 stk. 3.
          </p>
          <TableCookie rows={NECESSARY} />
        </Section>

        <Section id="analytics" title="4. Analytics-cookies (kræver samtykke)">
          <p>
            Vi bruger <strong>PostHog</strong> til at få anonym indsigt i, hvordan brugerne benytter hjemmesiden — så vi kan
            forbedre booking-flowet, fjerne fejl og bygge nye features baseret på reelle behov. Der lagres ikke noget der kan
            identificere dig (vi gemmer ikke navn, email eller IP-adresse i PostHog).
          </p>
          <TableCookie rows={ANALYTICS} />
          <p className="text-xs text-brand-text2 mt-2">
            Overførsel til PostHog Inc. (USA) sker under <strong>EU Standard Contractual Clauses (SCC 2021/914)</strong>
            samt under det amerikanske firmas tilslutning til EU-US Data Privacy Framework (DPF).
            Du kan altid fravælge analytics via knappen ovenfor.
          </p>
        </Section>

        <Section id="marketing" title="5. Marketing-cookies">
          <p>
            EMA Kurer ApS bruger pt. <strong>INGEN marketing- eller annoncerings-cookies</strong> (fx Facebook Pixel eller Google Ads).
            Hvis vi i fremtiden tilføjer disse, vil du blive bedt om særskilt samtykke før de aktiveres, og denne politik vil
            blive opdateret.
          </p>
        </Section>

        <Section id="tredjeparter" title="6. Tredjeparts-tjenester">
          <p>Følgende tjenester kan sætte cookies/localStorage når du bruger relaterede dele af sitet:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Stripe</strong> — sættes kun på betalingssiden. Krav iht. PSD2 (Strong Customer Authentication). Læs Stripes <a href="https://stripe.com/dk/cookie-settings" className="text-brand-red underline" target="_blank" rel="noreferrer">cookie-politik</a>.</li>
            <li><strong>Google Maps</strong> — vises kun på spore-siden (/track) når du har en aktiv pakke. Læs <a href="https://policies.google.com/technologies/cookies" className="text-brand-red underline" target="_blank" rel="noreferrer">Googles cookie-politik</a>.</li>
            <li><strong>OpenStreetMap</strong> — kort-tiles på offentlige sider. Sætter ikke tracking-cookies.</li>
          </ul>
        </Section>

        <Section id="dine-rettigheder" title="7. Dine rettigheder">
          <ul className="list-disc pl-6 space-y-1">
            <li>Du kan ændre dit samtykke når som helst via knappen ovenfor eller via "Cookies"-linket i footeren.</li>
            <li>Du kan slette cookies via din browsers indstillinger.</li>
            <li>Du kan klage til <strong>Datatilsynet</strong> hvis du mener vi ikke følger reglerne — se kontaktinfo i <Link to="/privatlivspolitik#klage" className="text-brand-red underline">privatlivspolitikken</Link>.</li>
          </ul>
        </Section>

        <Section id="aendringer" title="8. Ændringer i cookie-politikken">
          <p>
            Denne politik opdateres når vi tilføjer eller fjerner cookies, eller når lovgivningen ændres.
            Den aktuelle version er altid tilgængelig på <Link to="/cookie-politik" className="text-brand-red underline">emakurer.dk/cookie-politik</Link>.
          </p>
        </Section>

        <div className="mt-12 rounded-2xl border border-slate-200 bg-slate-50 p-6">
          <div className="flex items-start gap-3">
            <Mail size={20} className="text-brand-red shrink-0 mt-1" />
            <div>
              <h3 className="font-display text-lg font-bold text-brand-dark">Spørgsmål om cookies?</h3>
              <p className="text-sm text-brand-text2 mt-1">
                Skriv til <a href="mailto:kontakt@emakurer.dk" className="text-brand-red">kontakt@emakurer.dk</a> —
                vi svarer inden for én arbejdsdag.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
